import React, { useRef, useState } from "react";
import { Upload, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { PhotoCropDialog } from "./PhotoCropDialog";

const UploadIcon = ({ busy, done }) => {
  if (busy) return <Loader2 size={16} className="animate-spin" />;
  if (done) return <CheckCircle2 size={16} className="text-palm" />;
  return <Upload size={16} />;
};


const CROP_PURPOSES = new Set(["profile_photo", "officer_photo"]);

export function FileUpload({ purpose, onUploaded, accept = "image/*,application/pdf", label, testId = "file-upload", crop }) {
  const wantCrop = crop ?? CROP_PURPOSES.has(purpose);
  const [pending, setPending] = useState(null);
  const { t } = useLang();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error(`"${file.name}" is ${(file.size / 1048576).toFixed(1)} MB — the limit is 10 MB.`); e.target.value = ""; return; }
    e.target.value = "";
    if (wantCrop && file.type.startsWith("image/")) { setPending(file); return; }
    await send(file);
  };
  const send = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/files/upload?purpose=${purpose}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(data.filename);
      onUploaded?.(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      {pending && <PhotoCropDialog file={pending} onCancel={() => setPending(null)} onCropped={(f) => { setPending(null); send(f); }} />}
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={pick} data-testid={`${testId}-input`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testId}-btn`}
        className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gold/50 bg-parchment-card px-3 py-3 text-sm text-crimson transition-colors hover:border-gold hover:bg-gold-light disabled:opacity-60">
        <UploadIcon busy={busy} done={done} />
        {busy ? t("Uploading…") : done || label || t("Choose file")}
      </button>
    </div>
  );
}

export function AuthImage({ src, alt, className }) {
  const [url, setUrl] = useState(null);
  React.useEffect(() => {
    if (!src) return undefined;
    if (!src.startsWith("/api/")) { setUrl(src); return undefined; }
    let obj;
    let cancelled = false;
    api.get(src.replace(/^\/api/, ""), { responseType: "blob" })
      .then((r) => { if (cancelled) return; obj = URL.createObjectURL(r.data); setUrl(obj); })
      .catch(() => { if (!cancelled) setUrl(null); });
    return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
  }, [src]);
  if (!url) return null;
  return <img src={url} alt={alt} className={className} />;
}

export async function downloadPdf(path, filename) {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error("Could not generate PDF"); }
}
